import { IAutoplayInfo } from 'library/ts/interfaces/IAutoplayInfo';
import { checkAutoplay } from 'library/ts/utilities/Autoplay';
import { ConnectionMonitor } from 'library/ts/utilities/network/ConnectionMonitor';
import { ISmartTagOperationsDependencies } from 'tag/ts/smart_tag/operations/types/SmartTagOperationsTypes';
import { executeSmartTagReadyPageCallback } from 'tag/ts/smart_tag/utilities/Callback';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { isAviaErrorHandlerRegistered, registerAviaErrorHandler } from 'tag/ts/tag/player/adapters/avia/utilities/AviaErrorHandler';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Handles system operations (autoplay, connection monitoring)
 *
 * Manages system-level functionality including network connection monitoring
 * and autoplay capability detection. Uses defensive programming patterns
 * to ensure graceful degradation when system capabilities are unavailable.
 */
export class SmartTagSystemOperations {
	#deps: ISmartTagOperationsDependencies;

	constructor( deps: ISmartTagOperationsDependencies ) {
		this.#deps = deps;
	}

	/**
	 * @description Initialize connection monitor with error handling
	 */
	initializeConnectionMonitor = (): void => {
		if ( this.#deps.utilities && this.#deps.capabilities ) {
			// Tear down any prior monitor first so its online/offline window listeners and the
			// Firefox polling interval don't accumulate when a new monitor is created (SPA hosts).
			this.#deps.getConnectionMonitor()?.destroy();

			const monitor = new ConnectionMonitor(
				this.#deps.utilities,
				this.#deps.capabilities,
				this.#connectionManagerCallback.bind( this )
			);
			this.#deps.setConnectionMonitor( monitor );
		} else {
			this.#deps.logger.error( 'Cannot create ConnectionMonitor: utilities or capabilities not available' );
		}
	};

	/**
	 * @description Ensures the global avia `unhandledrejection` suppressor is registered. The handler
	 * is torn down when the registry empties (last removeTag / reset), so this re-arms it on create —
	 * a new tag's avia player still gets rejection suppression. No-op if already registered.
	 */
	ensureAviaErrorHandler = (): void => {
		if ( !isAviaErrorHandlerRegistered() ) {
			registerAviaErrorHandler();
		}
	};

	/**
	 * @description Handle autoplay detection based on settings
	 */
	handleAutoplayDetection = (): void => {
		switch ( this.#deps.detectAutoplay ) {
			case true:
				this.#runAutoplayDetection();
				break;
			case false:
				this.#executeReadyCallback( 'checkAutoplay SKIPPED: DETECT_AUTOPLAY=' + this.#deps.detectAutoplay );
				break;
		}
	};

	// Private helper methods

	/**
	 * @description Autoplay detection with graceful fallback handling
	 * Attempts capability-based autoplay detection but continues initialization
	 * even if capabilities are unavailable, ensuring system resilience.
	 */
	async #runAutoplayDetection(): Promise<void> {
		if ( this.#deps.capabilities ) {
			try {
				const autoplayInfo: IAutoplayInfo = await checkAutoplay( this.#deps.capabilities );
				this.#deps.logger.log( 'checkAutoplay SUCCESS:', autoplayInfo );
				this.#deps.setAutoplay( autoplayInfo );
				this.#executeReadyCallback();
			} catch ( error ) {
				this.#deps.logger.warn( 'checkAutoplay FAILURE:', error );
			}
		} else {
			this.#deps.logger.error( 'Cannot check autoplay: capabilities not available' );
			this.#executeReadyCallback( null, 'no capabilities' );
		}
	}

	/**
	 * @description Executes ready callback with comprehensive error handling
	 * Provides detailed error context for debugging callback execution failures.
	 */
	#executeReadyCallback( logMessage?: string, errorSuffix?: string ): void {
		try {
			if ( logMessage ) {
				this.#deps.logger.log( logMessage );
			}
			executeSmartTagReadyPageCallback( this.#deps.smartTagInstance );
		} catch ( error ) {
			const suffix = errorSuffix ? ` (${errorSuffix})` : '';
			this.#deps.logger.error( `Error executing ready callback${suffix}:`, error );
		}
	}

	/**
	 * @description Connection state change handler with defensive programming
	 * Bound arrow function preserves 'this' context when called by ConnectionMonitor.
	 * Implements comprehensive error handling for tag event dispatching to prevent
	 * individual tag failures from affecting overall connection state management.
	 */
	#connectionManagerCallback = ( online: boolean ): void => {
		try {
			const registry = this.#deps.getRegistry();
			if ( !registry ) {
				this.#deps.logger.warn( 'connectionManagerCallback:', 'Registry not available' );
				return;
			}

			Object.keys( registry ).forEach( ( key ) => {
				const tag = registry[ key ];
				if ( !tag?.dispatchTagEvent || !tag?.getTagEventVO ) {
					this.#deps.logger.warn( 'connectionManagerCallback:', 'Invalid tag structure for key:', key );
					return;
				}

				try {
					tag.dispatchTagEvent(
						tag.getTagEventVO(
							online === true ? TagEvent.ON_ONLINE : TagEvent.ON_OFFLINE,
							tag,
							{ online: online },
							this.#deps.smartTagInstance,
							'SmartTag'
						)
					);
				} catch ( eventError ) {
					this.#deps.logger.warn( 'connectionManagerCallback:', 'Error dispatching event for tag:', key, eventError );
				}
			} );
		} catch ( error ) {
			this.#deps.logger.error( 'connectionManagerCallback:', 'Unexpected error:', error );
		}
	};
}